import { useContext, useEffect, useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DefaultButton, Spinner, SpinnerSize } from '@fluentui/react'

import { getActiveJobs, getJobCandidateMatches, getJobApplications, getApplicationInterviews, updateInterview, ActiveJob, MatchResult, JobApplication, ScheduledInterview } from '../../api'
import { AppStateContext } from '../../state/AppProvider'

import styles from './ActiveJobs.module.css'

type View = 'list' | 'matches' | 'applications' | 'interviews'

const ActiveJobs = () => {
  const navigate = useNavigate()
  const appStateContext = useContext(AppStateContext)
  const [view, setView] = useState<View>('list')
  const [jobs, setJobs] = useState<ActiveJob[]>([])
  const [selectedJob, setSelectedJob] = useState<ActiveJob | null>(null)
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [matchLoading, setMatchLoading] = useState(false)
  const [applications, setApplications] = useState<JobApplication[]>([])
  const [applicationsLoading, setApplicationsLoading] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null)
  const [interviews, setInterviews] = useState<ScheduledInterview[]>([])
  const [interviewsLoading, setInterviewsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingInterview, setEditingInterview] = useState<number | null>(null)
  const [editOutcome, setEditOutcome] = useState('')
  const [editNotes, setEditNotes] = useState('')

  const viewRef = useRef(view)
  const selectedJobRef = useRef(selectedJob)
  viewRef.current = view
  selectedJobRef.current = selectedJob

  const loadJobs = useCallback(async () => {
    setLoading(true)
    const data = await getActiveJobs()
    setJobs(data)
    setLoading(false)
  }, [])

  const selectedApplicationRef = useRef(selectedApplication)
  selectedApplicationRef.current = selectedApplication

  const reloadApplications = useCallback(async (jobid: number) => {
    try {
      const result = await getJobApplications(jobid)
      setApplications(result)
    } catch (err) {
      // ignore
    }
  }, [])

  const reloadInterviews = useCallback(async (applicationid: number) => {
    try {
      const result = await getApplicationInterviews(applicationid)
      setInterviews(result)
    } catch (err) {
      // ignore
    }
  }, [])

  useEffect(() => {
    loadJobs()
  }, [])

  useEffect(() => {
    const handler = () => {
      loadJobs()
      if (viewRef.current === 'applications' && selectedJobRef.current) {
        reloadApplications(selectedJobRef.current.jobid)
      }
      if (viewRef.current === 'interviews' && selectedApplicationRef.current) {
        reloadInterviews(selectedApplicationRef.current.applicationid)
      }
    }
    window.addEventListener('app-data-changed', handler)
    return () => window.removeEventListener('app-data-changed', handler)
  }, [loadJobs, reloadApplications, reloadInterviews])

  useEffect(() => {
    let context = `User is viewing the Active Jobs page.`
    if (view === 'list' && jobs.length > 0) {
      context += ` ${jobs.length} jobs listed.`
    } else if (view === 'matches' && selectedJob) {
      context += ` Viewing candidate matches for job: ${selectedJob.title} at ${selectedJob.client_name || 'unknown client'}.`
    } else if (view === 'applications' && selectedJob) {
      context += ` Viewing applications for job: ${selectedJob.title}. ${applications.length} applications.`
    } else if (view === 'interviews' && selectedJob && selectedApplication) {
      context += ` Viewing interviews for ${selectedApplication.candidate_name} on job: ${selectedJob.title}. ${interviews.length} interviews scheduled.`
    }
    appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: context })
    return () => { appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: '' }) }
  }, [view, jobs.length, selectedJob, applications.length, selectedApplication, interviews.length])

  const handleFindCandidates = async (job: ActiveJob) => {
    setSelectedJob(job)
    setMatchResult(null)
    setError(null)
    setView('matches')
    setMatchLoading(true)
    try {
      const result = await getJobCandidateMatches(job.jobid)
      setMatchResult(result)
    } catch (err: any) {
      setError(err.message || 'Failed to find matching candidates. Please try again.')
    } finally {
      setMatchLoading(false)
    }
  }

  const handleViewApplications = async (job: ActiveJob) => {
    setSelectedJob(job)
    setApplications([])
    setSelectedApplication(null)
    setError(null)
    setView('applications')
    setApplicationsLoading(true)
    try {
      const result = await getJobApplications(job.jobid)
      setApplications(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load applications.')
    } finally {
      setApplicationsLoading(false)
    }
  }

  const handleViewApplicationInterviews = async (application: JobApplication) => {
    setSelectedApplication(application)
    setInterviews([])
    setError(null)
    setView('interviews')
    setInterviewsLoading(true)
    try {
      const result = await getApplicationInterviews(application.applicationid)
      setInterviews(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load interviews.')
    } finally {
      setInterviewsLoading(false)
    }
  }

  const startEditInterview = (iv: ScheduledInterview) => {
    setEditingInterview(iv.interviewid)
    setEditOutcome(iv.outcome || '')
    setEditNotes(iv.notes || '')
  }

  const cancelEditInterview = () => {
    setEditingInterview(null)
    setEditOutcome('')
    setEditNotes('')
  }

  const saveInterview = async (interviewid: number) => {
    const ok = await updateInterview(interviewid, { outcome: editOutcome, notes: editNotes })
    if (ok) {
      setInterviews(prev => prev.map(iv =>
        iv.interviewid === interviewid ? { ...iv, outcome: editOutcome, notes: editNotes } : iv
      ))
      setEditingInterview(null)
    }
  }

  const handleBack = () => {
    if (view === 'interviews') {
      // Back from interviews → applications
      setSelectedApplication(null)
      setInterviews([])
      setError(null)
      setView('applications')
    } else if (view === 'applications' || view === 'matches') {
      // Back from applications/matches → job list
      setSelectedJob(null)
      setApplications([])
      setMatchResult(null)
      setError(null)
      setView('list')
    } else {
      navigate('/')
    }
  }

  const getScoreClass = (score: number) => {
    if (score >= 70) return styles.scoreHigh
    if (score >= 40) return styles.scoreMedium
    return styles.scoreLow
  }

  const renderListView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={() => navigate('/')}
          title="Back to chat"
        />
        <h1 className={styles.title}>Active Jobs</h1>
      </div>
      {loading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading jobs..." />
        </div>
      ) : jobs.length === 0 ? (
        <div className={styles.emptyState}>No active jobs found.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th></th>
              <th>Title</th>
              <th>Client</th>
              <th>Location</th>
              <th>Skills</th>
              <th>Experience</th>
              <th>Salary</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(job => (
              <tr key={job.jobid}>
                <td className={styles.actionsCell}>
                  <button
                    className={styles.iconBtn}
                    onClick={() => handleFindCandidates(job)}
                    title="Find Candidates"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M6 1a5 5 0 0 1 3.94 8.06l4.5 4.5a.5.5 0 0 1-.71.71l-4.5-4.5A5 5 0 1 1 6 1zm0 1a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"/><path d="M10.5 11.5a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2a.5.5 0 0 1 .5-.5zm2-1a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 1 .5-.5z"/></svg>
                  </button>
                  <button
                    className={styles.iconBtn}
                    onClick={() => handleViewApplications(job)}
                    title="Applications"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M4.5 1A1.5 1.5 0 0 0 3 2.5v11A1.5 1.5 0 0 0 4.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-11A1.5 1.5 0 0 0 11.5 1h-7zM4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v11a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-11zM6 4h4v1H6V4zm0 2.5h4v1H6v-1zM6 9h4v1H6V9z"/></svg>
                  </button>
                </td>
                <td>{job.title}</td>
                <td>{job.client_name || '-'}</td>
                <td>{job.location || '-'}</td>
                <td>
                  {job.skills ? (
                    <span className={styles.skillsBadge} title={job.skills}>{job.skills}</span>
                  ) : '-'}
                </td>
                <td>{job.experience_level || '-'}</td>
                <td>{job.salary_range || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )

  const renderMatchesView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={handleBack}
          title="Back to job list"
        />
        <h1 className={styles.title}>Candidate Matching</h1>
      </div>

      {selectedJob && (
        <div className={styles.jobSummary}>
          <h2 className={styles.jobSummaryTitle}>{selectedJob.title}</h2>
          <div className={styles.jobSummaryMeta}>
            {selectedJob.location && <span>Location: {selectedJob.location}</span>}
            {selectedJob.skills && <span>Skills: {selectedJob.skills}</span>}
            {selectedJob.experience_level && <span>Level: {selectedJob.experience_level}</span>}
            {selectedJob.salary_range && <span>Salary: {selectedJob.salary_range}</span>}
          </div>
        </div>
      )}

      {matchLoading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="AI is analyzing candidates... This may take a moment." />
        </div>
      ) : error ? (
        <div className={styles.errorState}>{error}</div>
      ) : matchResult ? (
        <>
          <h3 className={styles.sectionTitle}>Top Matching Candidates</h3>
          {matchResult.top_candidates.length === 0 ? (
            <div className={styles.emptyState}>No matching candidates found for this job.</div>
          ) : (
            <div className={styles.matchCards}>
              {matchResult.top_candidates.map((candidate, index) => (
                <div key={candidate.candidateid} className={styles.matchCard}>
                  <div className={styles.matchRank}>
                    <span className={`${styles.matchScore} ${getScoreClass(candidate.match_score)}`}>
                      {candidate.match_score}
                    </span>
                    <span className={styles.matchScoreLabel}>#{index + 1}</span>
                  </div>
                  <div className={styles.matchInfo}>
                    <h4 className={styles.matchName}>{candidate.name}</h4>
                    <div className={styles.matchMeta}>
                      {candidate.location && <span>{candidate.location}</span>}
                      {candidate.skills && <span> &middot; {candidate.skills}</span>}
                    </div>
                    {candidate.reasons && candidate.reasons.length > 0 && (
                      <ul className={styles.matchReasons}>
                        {candidate.reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    )}
                    {candidate.distance_warning && (
                      <div className={styles.distanceWarning}>{candidate.distance_warning}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {matchResult.nearby_candidates.length > 0 && (
            <>
              <h3 className={styles.sectionTitle}>Nearby Candidates</h3>
              <div className={styles.nearbyList}>
                {matchResult.nearby_candidates.map(candidate => (
                  <div key={candidate.candidateid} className={styles.nearbyChip}>
                    <span>{candidate.name}</span>
                    <span className={styles.nearbyNote}>
                      {candidate.distance_note || candidate.location}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      ) : null}
    </>
  )

  const renderApplicationsView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={handleBack}
          title="Back to job list"
        />
        <h1 className={styles.title}>Applications</h1>
      </div>

      {selectedJob && (
        <div className={styles.jobSummary}>
          <h2 className={styles.jobSummaryTitle}>{selectedJob.title}</h2>
          <div className={styles.jobSummaryMeta}>
            {selectedJob.client_name && <span>Client: {selectedJob.client_name}</span>}
            {selectedJob.location && <span>Location: {selectedJob.location}</span>}
            {selectedJob.skills && <span>Skills: {selectedJob.skills}</span>}
          </div>
        </div>
      )}

      {applicationsLoading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading applications..." />
        </div>
      ) : error ? (
        <div className={styles.errorState}>{error}</div>
      ) : applications.length === 0 ? (
        <div className={styles.emptyState}>No applications found for this job.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Status</th>
              <th>Applied Date</th>
              <th>Stage</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {applications.map(app => (
              <tr key={app.applicationid}>
                <td>
                  <span className={styles.clickableLink} onClick={() => handleViewApplicationInterviews(app)}>
                    {app.candidate_name}
                  </span>
                </td>
                <td>{app.status || '-'}</td>
                <td>{app.applied_date || '-'}</td>
                <td>{app.stage || '-'}</td>
                <td>{app.notes || '-'}</td>
                <td>
                  {app.has_interviews && (
                    <span className={styles.interviewIndicator} title="Has scheduled interviews">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="#107c10"><path d="M4.5 0A1.5 1.5 0 0 0 3 1.5V2H2.5A1.5 1.5 0 0 0 1 3.5v10A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-10A1.5 1.5 0 0 0 13.5 2H13v-.5A1.5 1.5 0 0 0 11.5 0h-7zM4 1.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5V2H4v-.5zM2 3.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 .5.5V5H2V3.5zM2 6h12v7.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5V6z"/></svg>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )

  const renderInterviewsView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={handleBack}
          title="Back to applications"
        />
        <h1 className={styles.title}>Scheduled Interviews</h1>
      </div>

      {selectedJob && selectedApplication && (
        <div className={styles.jobSummary}>
          <h2 className={styles.jobSummaryTitle}>{selectedApplication.candidate_name}</h2>
          <div className={styles.jobSummaryMeta}>
            <span>Job: {selectedJob.title}</span>
            {selectedJob.client_name && <span>Client: {selectedJob.client_name}</span>}
            {selectedJob.location && <span>Location: {selectedJob.location}</span>}
          </div>
        </div>
      )}

      {interviewsLoading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading interviews..." />
        </div>
      ) : error ? (
        <div className={styles.errorState}>{error}</div>
      ) : interviews.length === 0 ? (
        <div className={styles.emptyState}>No interviews scheduled for this application.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Date</th>
              <th>Time</th>
              <th>Type</th>
              <th>Location</th>
              <th>Outcome</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {interviews.map(iv => (
              <tr key={iv.interviewid}>
                <td>{iv.candidate_name}</td>
                <td>{iv.interview_date || '-'}</td>
                <td>{iv.interview_time || '-'}</td>
                <td>{iv.interview_type || '-'}</td>
                <td>{iv.location || '-'}</td>
                {editingInterview === iv.interviewid ? (
                  <>
                    <td>
                      <input
                        className={styles.editInput}
                        value={editOutcome}
                        onChange={e => setEditOutcome(e.target.value)}
                        placeholder="Outcome"
                      />
                    </td>
                    <td>
                      <input
                        className={styles.editInput}
                        value={editNotes}
                        onChange={e => setEditNotes(e.target.value)}
                        placeholder="Notes"
                      />
                    </td>
                    <td>
                      <div className={styles.editActions}>
                        <DefaultButton text="Save" className={styles.saveButton} onClick={() => saveInterview(iv.interviewid)} />
                        <DefaultButton text="Cancel" onClick={cancelEditInterview} />
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{iv.outcome || '-'}</td>
                    <td>{iv.notes || '-'}</td>
                    <td>
                      <DefaultButton text="Edit" className={styles.editButton} onClick={() => startEditInterview(iv)} />
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )

  return (
    <div className={styles.container}>
      {view === 'list' && renderListView()}
      {view === 'matches' && renderMatchesView()}
      {view === 'applications' && renderApplicationsView()}
      {view === 'interviews' && renderInterviewsView()}
    </div>
  )
}

export default ActiveJobs
